<template>
    <BaseCard class="flex flex-col gap-5 overflow-hidden text-ink-muted">
        <div class="flex gap-5 flex-1">
            <div class="flex flex-col gap-3 flex-1">
                <div class="nadia-subtitle text-pretty text-ink">{{ t("chat.home.profileCompletion.title") }}</div>
                <div class="body-small text-ink-muted mt-1">{{ t("chat.home.profileCompletion.body") }}</div>
                <div class="flex flex-col gap-2 mt-1">
                    <div v-for="section in sections" :key="section.id" class="flex items-center gap-2.5">
                        <component :is="section.icon" class="h-3.5 w-6 shrink-0" :class="section.complete ? 'opacity-40' : ''" />
                        <span class="body-small flex-1" :class="section.complete ? 'text-ink-muted line-through' : 'text-ink'">
                            {{ section.title }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="mt-auto">
            <BaseButton theme="neutral" class="w-full" @click="handleClick">{{ ctaText }}</BaseButton>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { RecordUiInteractionRequest } from "~vue/api/generated/ui-interactions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import TileGreen from "~vue/icons/tiles/TileGreen.vue";
import TilePink from "~vue/icons/tiles/TilePink.vue";
import TileTeal from "~vue/icons/tiles/TileTeal.vue";
import type { ProfileCompletionSection } from "~vue/UserProfile/types";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type Component, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
    cardSections: ProfileCompletionSection[];
}>();

const { t } = useI18n();

const iconMap: Record<string, Component> = {
    "getting-to-know-you": TilePink,
    "getting-to-know-your-team": TileGreen,
    "understanding-how-you-work": TileTeal,
};

const sections = computed(() =>
    props.cardSections.map((s) => ({
        ...s,
        icon: iconMap[s.id] ?? TilePink,
    })),
);

const hasAnyCompleted = computed(() => props.cardSections.some((s) => s.completed_count > 0));
// Analytics `cta_label` values, kept in English in every language; the button displays the translated label.
const ctaLabel = computed(() => (hasAnyCompleted.value ? "Continue" : "Get Started"));
const ctaText = computed(() => (hasAnyCompleted.value ? t("chat.home.profileCompletion.continue") : t("chat.home.profileCompletion.getStarted")));

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.PROFILE_COMPLETION_CARD_SHOWN, {
        sections_total: props.cardSections.length,
        sections_complete: props.cardSections.filter((s) => s.complete).length,
        cta_label: ctaLabel.value,
    });
});

const { execute: recordCtaClick } = useFetch("/v1/ui-interactions", { immediate: false })
    .post({ interaction_type: "profile_completion_card_cta_clicked" } satisfies RecordUiInteractionRequest)
    .json();

const handleClick = () => {
    logUserInteraction(AmplitudeEventNames.PROFILE_COMPLETION_CARD_CLICKED, {
        sections_total: props.cardSections.length,
        sections_complete: props.cardSections.filter((s) => s.complete).length,
        cta_label: ctaLabel.value,
    });
    recordCtaClick();
    router.visit("/accounts/user-profile");
};
</script>
